import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PROJECTS_MIN_ACTIVE, type ProjectItem, type RuleGroup } from '@crm/shared';
import { projectStore } from '../../api/client';
import { useProjects } from './api';

const STORAGE_KEY = 'crm.project';

interface ProjectScope {
  projects: ProjectItem[];
  /** the project list has loaded (or failed), so `current` can be trusted for defaults */
  ready: boolean;
  /** projects switch on (labels, selector, scoping) once the workspace has two or more */
  enabled: boolean;
  /** the project picked in the header, or null for all projects */
  current: ProjectItem | null;
  select: (id: string | null) => void;
  byId: (id: string | null | undefined) => ProjectItem | undefined;
}

const Ctx = createContext<ProjectScope>({ projects: [], ready: false, enabled: false, current: null, select: () => undefined, byId: () => undefined });

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Holds the header's project selection (remembered per browser) and puts it on every API request. A selection that
 * no longer exists, or projects being switched off (fewer than two), falls back to all projects.
 */
export function ProjectScopeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const projects = useProjects();
  const [selected, setSelected] = useState<string | null>(() => {
    const stored = readStored();
    projectStore.set(stored);
    return stored;
  });
  const list = useMemo(() => projects.data ?? [], [projects.data]);
  const enabled = list.length >= PROJECTS_MIN_ACTIVE;
  const current = (enabled && list.find((p) => p.id === selected)) || null;

  const select = useCallback(
    (id: string | null) => {
      setSelected(id);
      projectStore.set(id);
      try {
        if (id) localStorage.setItem(STORAGE_KEY, id);
        else localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* storage unavailable: the selection lasts for this tab */
      }
      // every list may narrow differently now
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'projects' });
    },
    [qc],
  );

  // drop a stale selection once the project list is known
  useEffect(() => {
    if (projects.data && selected && !current) select(null);
  }, [projects.data, selected, current, select]);

  const ready = projects.isFetched;
  const value = useMemo<ProjectScope>(() => ({ projects: list, ready, enabled, current, select, byId: (id) => (id ? list.find((p) => p.id === id) : undefined) }), [list, ready, enabled, current, select]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useProjectScope = () => useContext(Ctx);

/** Narrows a contacts filter to the selected project's tags, lists and segments (unchanged for all projects). */
export function useScopedFilter(filter: RuleGroup | undefined): RuleGroup | undefined {
  const { current } = useProjectScope();
  return useMemo(() => {
    if (!current) return filter;
    return { combinator: 'and', conditions: [{ kind: 'project', projectId: current.id }, ...(filter?.conditions.length ? [filter] : [])] };
  }, [current, filter]);
}
